import { sanitizeHtml } from '@realadvisor/html-sanitizer';

// The one sanitizer call for email HTML that lands in a sandboxed EmailBody
// iframe (templates, campaigns, inbox threads). On top of the shared
// blacklist sanitizer, two things the iframe itself cannot contain:
//
// - `<html>`, `<head>`, `<body>` (and `<title>`, whose text would render as
//   prose): the sanitizer keeps them, and the HTML parser MERGES the
//   attributes of a nested `<body style=…>` onto the document's real body, so
//   one message could repaint the whole iframe, sibling messages and CRM
//   chrome included. The tags go, their children stay.
// - `target` and `ping` on links: `<base target="_blank">` plus the sandbox
//   turn clicks into no-ops, but an explicit `target="_self"` on the link
//   wins over the base and navigates the iframe itself, which both fires a
//   tracker on click and replaces the preview with the linked page.
//
// Both rewrites run on the SANITIZER'S OUTPUT, whose tags are re-emitted in a
// fixed shape (`<name attr="encoded">`: no `>` or `"` inside a value, and a
// `<` in text is escaped), which is what makes a regex safe here where it
// would not be on raw input. The attribute rewrite walks the attribute list
// of each tag attribute by attribute, so the word `target` in a text node or
// inside an `alt` / `title` value is left alone. Idempotent on its own
// output.
const ROOT_TAG_RE = /<\/?(?:html|head|body)\b[^>]*>/gi;
// A title holds text only: the shared sanitizer auto-closes an unclosed
// <title> at the END of the document, so one wrapping markup is the head of
// a mail whose body must stay. Its tags then go like the root tags below.
const TITLE_RE = /<title\b[^>]*>[^<]*<\/title>/gi;
const TITLE_TAG_RE = /<\/?title\b[^>]*>/gi;
// `<name attr="value" attr ... />`: the sanitizer's own emission shape.
const TAG_RE = /<([a-z][^\s>/]*)((?:\s+[a-z0-9:_-]+(?:="[^"]*")?)*)\s*(\/?)>/gi;
// One attribute, value included: a global replace walks the list in order,
// each match consuming its whole value, so a `target` inside a value is never
// at a match start. With or without a value: the sanitizer re-emits an empty
// value as the bare attribute name, and an empty `target` resolves to `_self`
// all the same.
const ATTR_RE = /\s+([a-z0-9:_-]+)(?:="[^"]*")?/gi;
const NAVIGATION_ATTRS = new Set(['target', 'ping']);

const dropNavigationAttrs = (attributes: string): string =>
  attributes.replace(ATTR_RE, (attribute, name: string) =>
    NAVIGATION_ATTRS.has(name.toLowerCase()) ? '' : attribute,
  );

export const sanitizeEmailHtml = (html: string): string =>
  sanitizeHtml(html)
    .replace(TITLE_RE, '')
    .replace(TITLE_TAG_RE, '')
    .replace(ROOT_TAG_RE, '')
    .replace(
      TAG_RE,
      (_tag, name: string, attributes: string, slash: string) =>
        `<${name}${dropNavigationAttrs(attributes)}${slash}>`,
    );
